'use client'

import { FC, useEffect, useState } from 'react'
import { cn } from '@utils/tailwind.utils'
import { MANDATORY_COUNTDOWN_SECONDS, useAppUpdate } from './AppUpdateContext'

const RefreshIcon: FC<{ spinning?: boolean }> = ({ spinning }) => (
  <svg
    className={cn('h-4 w-4 shrink-0', spinning && 'animate-spin')}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <polyline points="21 3 21 9 15 9" />
  </svg>
)

/**
 * Compact update card, pinned bottom-left.
 *
 * Shown when the deployed build id differs from the one this bundle was built
 * with. A mandatory release drops the "Later" action and counts down to an
 * automatic update; everything else is dismissable for the tab session.
 *
 * Deliberately not a blocking modal — the mandatory path refreshes on its own,
 * so taking over the screen buys nothing and interrupts whatever the user is
 * mid-way through.
 */
export const AppUpdateBanner: FC = () => {
  const { updateAvailable, mandatory, countdown, remote, dismiss, refresh } =
    useAppUpdate()
  const [updating, setUpdating] = useState(false)
  const [entered, setEntered] = useState(false)

  useEffect(() => {
    if (!updateAvailable) {
      setEntered(false)
      return
    }
    const frame = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [updateAvailable])

  if (!updateAvailable || !remote) return null

  const onUpdate = () => {
    if (updating) return
    setUpdating(true)
    void refresh()
  }

  const highlights = remote.highlights.slice(0, 2)
  const progress =
    countdown === null
      ? 0
      : ((MANDATORY_COUNTDOWN_SECONDS - countdown) / MANDATORY_COUNTDOWN_SECONDS) * 100

  return (
    <div
      className={cn(
        // Above the app's whole overlay ladder — the highest in use is the equity
        // success modal at z-[10060], with the chatbot overlay at z-[10001] and
        // portfolio drawers at z-[300]. An update the user cannot see is an update
        // that does not happen, and a mandatory one reloads the page regardless, so
        // it has to be visible over whatever is open. The card is small and
        // bottom-left, and modals here are centred, so it obscures very little.
        'pointer-events-none fixed z-[10070] left-4 right-4 sm:right-auto sm:w-[340px]',
        // Clears the mobile tab bar, which the app layouts reserve 55px for.
        'bottom-[calc(55px+1rem+env(safe-area-inset-bottom))] sm:bottom-[max(1rem,env(safe-area-inset-bottom))]',
      )}
    >
      <div
        className={cn(
          'pointer-events-auto overflow-hidden rounded-2xl',
          'bg-cw-midnight-purple/95 border border-white/10 shadow-xl backdrop-blur-md',
          'transition-all duration-200 ease-out',
          entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
        )}
        role="status"
        aria-live="polite"
        aria-labelledby="app-update-title"
      >
        <div className="p-4">
          <div className="flex items-center gap-2.5">
            <span className="bg-cw-electric-purple/20 text-cw-purple-bright flex h-7 w-7 shrink-0 items-center justify-center rounded-lg">
              <RefreshIcon spinning={updating || mandatory} />
            </span>
            <p
              id="app-update-title"
              className="font-funnel min-w-0 flex-1 truncate text-sm font-semibold text-white"
            >
              {mandatory ? 'Updating WLTH' : 'Update available'}
            </p>
            {remote.version && (
              <span className="text-cw-grey-grey-400 shrink-0 text-[11px] tabular-nums">
                v{remote.version}
              </span>
            )}
          </div>

          <p className="text-cw-grey-grey-300 mt-2 text-xs leading-relaxed">
            {mandatory
              ? 'Everyone needs this version. Refreshing your cached app now.'
              : remote.title || 'A newer version of WLTH has been released.'}
          </p>

          {!mandatory && highlights.length > 0 && (
            <ul className="mt-2 space-y-1">
              {highlights.map((item) => (
                <li
                  key={item}
                  className="text-cw-grey-grey-400 flex gap-2 text-[11px] leading-snug"
                >
                  <span
                    className="bg-cw-electric-purple mt-[5px] h-1 w-1 shrink-0 rounded-full"
                    aria-hidden="true"
                  />
                  <span className="min-w-0">{item}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 flex items-center justify-between gap-2">
            <a
              href="/changelog"
              className="text-cw-grey-grey-400 hover:text-cw-purple-bright text-[11px] underline-offset-2 transition-colors hover:underline"
            >
              What&apos;s new
            </a>

            <div className="flex items-center gap-1">
              {!mandatory && (
                <button
                  type="button"
                  onClick={dismiss}
                  disabled={updating}
                  className="text-cw-grey-grey-300 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors hover:bg-white/10 hover:text-white disabled:opacity-50"
                >
                  Later
                </button>
              )}
              <button
                type="button"
                onClick={onUpdate}
                disabled={updating}
                className="bg-cw-electric-purple hover:bg-cw-electric-purple/85 font-funnel rounded-lg px-3.5 py-1.5 text-xs font-semibold text-white transition-colors disabled:opacity-60"
              >
                {updating
                  ? 'Updating…'
                  : mandatory && countdown !== null
                    ? `Update (${countdown}s)`
                    : 'Update'}
              </button>
            </div>
          </div>
        </div>

        {mandatory && (
          <div className="h-0.5 w-full bg-white/10" aria-hidden="true">
            <div
              className="bg-cw-electric-purple h-full transition-[width] duration-1000 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  )
}
